import type { RequestOptions } from '../client.js';
import type { ChainSlug } from '../types/chains.js';
import type { HistoricalQueryOptions } from '../types/client.js';
import {
  type NftTokenOwnerData,
  getNftCollectionParamsSchema,
  getNftBalanceParamsSchema,
  getNftTokensParamsSchema,
  getNftMetadataParamsSchema,
  getNftTokenOwnerParamsSchema,
  getNftErc1155BalanceParamsSchema,
  getNftBatchBalanceParamsSchema,
} from '@spectrum-nodes/api-spec/models';
import type {
  NftCollectionResult,
  NftBalanceResult,
  NftTokensResult,
  NftBatchBalanceResult,
  NftTokenMetadataResult,
} from '../types/index.js';
import { parseParams } from '../internals/validation.js';
import { splitHistoricalArgs } from '../internals/utils.js';
import { ChainNamespace } from './base.js';

/** NFT collection data, balances, ownership, and metadata */
export class NftsNamespace extends ChainNamespace {
  /** Get NFT collection info. */
  async getCollection(
    chain: ChainSlug | undefined,
    contract: string,
    paramsOrOptions?: HistoricalQueryOptions | RequestOptions,
    options?: RequestOptions,
  ): Promise<NftCollectionResult> {
    const { params, options: requestOptions } = splitHistoricalArgs(paramsOrOptions, options);
    const validated = parseParams(getNftCollectionParamsSchema, {
      chain: this.resolve(chain),
      contract,
      blockHeight: params?.blockHeight,
    });
    return this.client.jsonRpc<NftCollectionResult>('getNftCollection', validated, requestOptions);
  }

  /** Get NFT balance (count) for an address. */
  async getBalance(
    chain: ChainSlug | undefined,
    contract: string,
    address: string,
    paramsOrOptions?: HistoricalQueryOptions | RequestOptions,
    options?: RequestOptions,
  ): Promise<NftBalanceResult> {
    const { params, options: requestOptions } = splitHistoricalArgs(paramsOrOptions, options);
    const validated = parseParams(getNftBalanceParamsSchema, {
      chain: this.resolve(chain),
      contract,
      address,
      blockHeight: params?.blockHeight,
    });
    return this.client.jsonRpc<NftBalanceResult>('getNftBalance', validated, requestOptions);
  }

  /** Get token IDs owned by an address (ERC-721 Enumerable only). */
  async getOwnedTokens(
    chain: ChainSlug | undefined,
    contract: string,
    address: string,
    paramsOrOptions?: HistoricalQueryOptions | RequestOptions,
    options?: RequestOptions,
  ): Promise<NftTokensResult> {
    const { params, options: requestOptions } = splitHistoricalArgs(paramsOrOptions, options);
    const validated = parseParams(getNftTokensParamsSchema, {
      chain: this.resolve(chain),
      contract,
      address,
      blockHeight: params?.blockHeight,
    });
    return this.client.jsonRpc<NftTokensResult>('getNftTokens', validated, requestOptions);
  }

  /** Batch balance query for ERC-1155 tokens. Max 100 pairs. */
  async getBatchBalance(
    chain: ChainSlug | undefined,
    contract: string,
    params: { addresses: string[]; tokenIds: string[] },
    options?: RequestOptions,
  ): Promise<NftBatchBalanceResult> {
    const validated = parseParams(getNftBatchBalanceParamsSchema, {
      chain: this.resolve(chain),
      contract,
      addresses: params.addresses,
      tokenIds: params.tokenIds,
    });
    return this.client.jsonRpc<NftBatchBalanceResult>('getNftBatchBalance', validated, options);
  }

  /** Get ERC-1155 balance for a specific token ID. */
  async getTokenBalance(
    chain: ChainSlug | undefined,
    contract: string,
    address: string,
    tokenId: string,
    options?: RequestOptions,
  ): Promise<NftBalanceResult> {
    const validated = parseParams(getNftErc1155BalanceParamsSchema, {
      chain: this.resolve(chain),
      contract,
      address,
      tokenId,
    });
    return this.client.jsonRpc<NftBalanceResult>('getNftErc1155Balance', validated, options);
  }

  /** Get owner of a specific token ID. */
  async getTokenOwner(
    chain: ChainSlug | undefined,
    contract: string,
    tokenId: string,
    options?: RequestOptions,
  ): Promise<NftTokenOwnerData> {
    const validated = parseParams(getNftTokenOwnerParamsSchema, {
      chain: this.resolve(chain),
      contract,
      tokenId,
    });
    return this.client.jsonRpc<NftTokenOwnerData>('getNftTokenOwner', validated, options);
  }

  /** Get metadata for a specific token ID. */
  async getTokenMetadata(
    chain: ChainSlug | undefined,
    contract: string,
    tokenId: string,
    paramsOrOptions?: HistoricalQueryOptions | RequestOptions,
    options?: RequestOptions,
  ): Promise<NftTokenMetadataResult> {
    const { params, options: requestOptions } = splitHistoricalArgs(paramsOrOptions, options);
    const validated = parseParams(getNftMetadataParamsSchema, {
      chain: this.resolve(chain),
      contract,
      tokenId,
      blockHeight: params?.blockHeight,
    });
    return this.client.jsonRpc<NftTokenMetadataResult>('getNftMetadata', validated, requestOptions);
  }
}
